/**
 * Pure snap decision for the cart bottom-sheet drag gesture. The component owns
 * the DOM (live transform during the drag); this only decides the resting state
 * once the finger lifts. Distance OR a fast flick can decide it.
 *
 * Conventions: down = positive (dyPx, velocity). `dyPx` is the total travel from
 * drag start; `velocityPxPerMs` is the recent signed speed at release.
 */
export interface SheetSnapInput {
  startOpen: boolean;
  dyPx: number;
  sheetHeightPx: number;
  velocityPxPerMs: number;
}

/** Fraction of sheet height the finger must cross to flip state on distance alone. */
export const SHEET_POSITION_FRACTION = 0.35;
/** |velocity| (px/ms) above which a flick decides the direction regardless of distance. */
export const SHEET_FLICK_VELOCITY = 0.5;

export function computeSheetSnap(input: SheetSnapInput): 'open' | 'closed' {
  const { startOpen, dyPx, sheetHeightPx, velocityPxPerMs } = input;

  // A decisive flick wins over position.
  if (velocityPxPerMs <= -SHEET_FLICK_VELOCITY) return 'open';
  if (velocityPxPerMs >= SHEET_FLICK_VELOCITY) return 'closed';

  const threshold = sheetHeightPx * SHEET_POSITION_FRACTION;
  if (startOpen) {
    // Dragging DOWN (positive) far enough closes; otherwise snap back open.
    return dyPx >= threshold ? 'closed' : 'open';
  }
  // Closed: dragging UP (negative) far enough opens; otherwise snap back closed.
  return -dyPx >= threshold ? 'open' : 'closed';
}
